import { IRANIAN_WOMEN_CAROUSEL } from '../constants'
import { IranianWomenItemViewModel } from '../types'

interface CarouselLayout {
    baseItems: IranianWomenItemViewModel[]
    originalLength: number
    visibleSideCount: number
    perView: number
    slides: IranianWomenItemViewModel[]
    totalSlides: number
    originalIndex: number
    activeItem: IranianWomenItemViewModel | undefined
}

interface DeviceVariant<T> {
    mobile: T
    desktop: T
}

export const byDevice = <T>(variant: DeviceVariant<T>, isMobile: boolean): T =>
    isMobile ? variant.mobile : variant.desktop

const computeVisibleSideCount = (originalLength: number, isMobile: boolean): number => {
    const maxSide = byDevice(IRANIAN_WOMEN_CAROUSEL.maxVisibleSideCount, isMobile)

    return originalLength <= 1 ? 0 : Math.min(maxSide, Math.max(1, Math.floor((originalLength - 1) / 2)))
}

export const computePerView = (visibleSideCount: number, isMobile: boolean): number => {
    const { min, mobile, desktop } = IRANIAN_WOMEN_CAROUSEL.perViewFormula
    const formula = isMobile ? mobile : desktop

    return Math.max(min, visibleSideCount * formula.visibleSideMultiplier + formula.offset)
}

const computeRepeatedSlides = (
    baseItems: IranianWomenItemViewModel[],
    perView: number
): IranianWomenItemViewModel[] => {
    const originalLength = baseItems.length
    const repeatTimes = originalLength > 0 ? Math.max(1, Math.ceil((perView * 3) / originalLength)) : 1

    return repeatTimes > 1 ? Array.from({ length: repeatTimes }, () => baseItems).flat() : baseItems
}

export const computeOriginalIndex = (activeIndex: number, originalLength: number): number =>
    originalLength ? ((activeIndex % originalLength) + originalLength) % originalLength : 0

export const computeCarouselLayout = (
    filteredItems: IranianWomenItemViewModel[] | undefined,
    isMobile: boolean,
    activeIndex: number
): CarouselLayout => {
    const baseItems = filteredItems ?? []
    const originalLength = baseItems.length
    const visibleSideCount = computeVisibleSideCount(originalLength, isMobile)
    const perView = computePerView(visibleSideCount, isMobile)
    const slides = computeRepeatedSlides(baseItems, perView)
    const totalSlides = slides.length
    const originalIndex = computeOriginalIndex(activeIndex, originalLength)
    const activeItem = baseItems[originalIndex]

    return {
        baseItems,
        originalLength,
        visibleSideCount,
        perView,
        slides,
        totalSlides,
        originalIndex,
        activeItem,
    }
}
